import { getCSSVariable } from "@circle-react/helpers/cssVariableHelpers";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import type { Appearance } from "@stripe/stripe-js";
import { useMemo } from "react";

import {
  appearanceThemes,
  useThemeContextForStripeElements,
} from "./useThemeContext";

// All consumers use 16px text on mobile to prevent focus zoom and 14px on desktop.
// A fixed line-height keeps field height unchanged across font sizes.
const fieldStyles = {
  fontWeight: "400",
  lineHeight: "20px",
  letterSpacing: "0",
  padding: "7.1666667px 12px",
  borderRadius: "6px",
  boxShadow: "none",
  margin: "0",
};

export const useCompassAppearance = (): Pick<
  Appearance,
  "variables" | "rules"
> => {
  const { stripeTheme } = useThemeContextForStripeElements();
  const isMobile = useIsMobile();
  const inputFontSize = isMobile ? "16px" : "14px";

  return useMemo(() => {
    const theme = stripeTheme === appearanceThemes.night ? "dark" : "light";
    const hoverBorderColor =
      getCSSVariable("--color-border-hover", theme) || "#d2d7db";
    const focusBorderColor =
      getCSSVariable("--color-border-focus", theme) || "#717680";
    const borderColor =
      getCSSVariable("--color-border-primary", theme) || "#e4e7eb";
    const textColor =
      getCSSVariable("--color-text-primary", theme) ||
      (stripeTheme === appearanceThemes.night ? "#F0F3F5" : "#19283a");
    const secondaryTextColor =
      getCSSVariable("--color-text-secondary", theme) ||
      (stripeTheme === appearanceThemes.night ? "#C7D0D8" : "#697586");

    return {
      variables: {
        colorPrimary: focusBorderColor,
        colorText: textColor,
        colorTextSecondary: secondaryTextColor,
        spacingGridRow: "21px",
      },
      rules: {
        [".Input"]: { ...fieldStyles, fontSize: inputFontSize },
        [".Input:hover"]: { border: `1px solid ${hoverBorderColor}` },
        [".Input:focus"]: {
          border: `1px solid ${focusBorderColor}`,
          boxShadow: `0 0 0 3px ${focusBorderColor}4D`,
        },
        [".Select"]: {
          ...fieldStyles,
          fontSize: inputFontSize,
          border: `1px solid ${borderColor}`,
        },
        [".Select:hover"]: { border: `1px solid ${hoverBorderColor}` },
        [".Select:focus"]: {
          border: `1px solid ${focusBorderColor}`,
          boxShadow: `0 0 0 3px ${focusBorderColor}4D`,
        },
        [".Tab"]: {
          color: textColor,
        },
        [".TabLabel"]: {
          color: textColor,
        },
        [".PickerItem"]: {
          color: textColor,
        },
        [".Label"]: {
          fontSize: "14px",
          fontWeight: "500",
          lineHeight: "20px",
          color: textColor,
          margin: "0 0 8px 0",
        },
      },
    };
  }, [inputFontSize, stripeTheme]);
};
